/**
 * S 域（投资）**端侧纯逻辑** —— 分组 / 展示文本 / 草稿校验与请求构造（批 `99` B2）
 *
 * 依据：`投资域PRD_持仓与买卖` **v2** §4.2（三段）· §5（品种 × 动作矩阵）·
 * §6（口径细则）· §8（16 AC）· `DR-059` D2/D3/D4/D6/D7 · 铁律 24/31/63/103。
 *
 * ## 🔴 本文件"一个业务数都不算"
 *
 * | 数 | 谁算 | 本文件做什么 |
 * |---|---|---|
 * | 六个汇总数 / 三档回报率 / 年化 | **服务端**（`nucleus/investment.ts` + `money.ts:xirrPctE4`） | 只把下发的整数搬成文本 |
 * | 成本 / 市值 / 浮盈 / 浮盈率 / 占比 | **服务端**（`HoldingView` 现成字段） | 只渲染 |
 * | 份额 ⇄ 金额（金额制折算） | **内核** `amountToQtyE4` / `priceToCents` | 只调，不自己除 |
 *
 * ## 🔴 唯一"像计算"的三处，都不是业务算术
 *
 * 1. {@link previewTrade}：调内核把"用户填的量"折成 `(quantityE4, amountCents)`，
 *    用于**提交前回显** —— `AC-INV11` 要求"实际到账 ≠ 输入金额时必须回显真实值"；
 * 2. {@link maturityTextOf}：`daysBetween` 算剩余天数（日历差，不是金额）；
 * 3. {@link buildTradeRequest} 等：组装请求体（可选键按需剔键，不发 `undefined` —— 铁律 63）。
 *
 * ## 🔴 三条不在端侧重判的纪律
 *
 * - **回报率是否算得出来**由服务端给 `null`（`AC-INV7`/`AC-INV12`）⇒ 端侧只把 `null` 显示成 `—`，
 *   **绝不** `?? 0`（那会把"算不出来"伪装成"没赚钱"）。
 * - **闭合差**由服务端给 `closingDiffCents`（历史数据实测 39 分）⇒ 端侧只负责"非 0 就提示"。
 * - **资金账户币种、卖出不超持仓**等服务端都判（`AC-INV1`/`AC-INV3`）；端侧只拦
 *   "表单里一眼看得出的不一致"（没填、负数、选了不同币种的账户），避免让用户白跑一次 400。
 */

import {
  AMOUNT_UNIT_KINDS,
  HoldingKind,
  IncomeKind,
  MATURITY_KINDS,
  TradeKind,
  amountToPriceE4,
  amountToQty2E4,
  centsToYuanText,
  daysBetween,
  e4TextToInt,
  e4ToText,
  e4ToTrimmedText,
  formatMoneyText,
  grossToleranceCents,
  isDayString,
  isRealDayString,
  priceToCents,
  toCents,
  type BatchUpdatePriceRequest,
  type CreateTradeRequest,
  type CurrencySummaryView,
  type HoldingView,
  type PriceE4,
  type QtyE4,
  type ReconcileCurrencyView,
  type RecordInvestmentIncome,
  type UpdateHoldingPriceRequest,
  type XirrView,
} from '@jiaying/shared';
import {
  ACTION_FALLBACK,
  ACTION_OF_KIND,
  KIND_LABEL,
  KIND_ORDER,
  MATURITY_UNFILLED_TEXT,
  RECONCILE_GAIN,
  RECONCILE_LOSS,
  RECONCILE_NOTE_HISTORY,
  RECONCILE_NOTE_SHARED,
  RECONCILE_NOTE_UNKNOWN,
  RECONCILE_NOT_COMPARABLE,
  RECONCILE_OK,
  UNASSIGNED_PLATFORM,
  XIRR_HINT_PREFIX,
  XIRR_HINT_SUFFIX,
} from './investment-labels.js';
import { percentWithoutSign, percentWithSign } from './percent-text.js';
import { yuanTextToCentsOrNull } from './txn-draft.js';

/** brand 转换（与 `nucleus/investment.ts` 同款：只在边界处转一次，内部全用 number） */
const asQty = (n: number): QtyE4 => n as QtyE4;
const asPrice = (n: number): PriceE4 => n as PriceE4;

// ─────────────────────────── ① 品种 × 动作 ───────────────────────────

/** 按品种取动作文案（未知品种按「其他」—— 历史脏值不该让页面炸掉） */
export function actionsOf(kind: string): NonNullable<(typeof ACTION_OF_KIND)[string]> {
  return ACTION_OF_KIND[kind] ?? ACTION_FALLBACK;
}

/** 品种中文名（未知品种原样返回枚举值，便于排查脏数据） */
export function kindLabelOf(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}

/** 金额制品种（理财 / 存款）：录入的是"多少钱"（PRD §5） */
export function isAmountMode(kind: string): boolean {
  return (AMOUNT_UNIT_KINDS as readonly string[]).includes(kind);
}

/** 有到期概念的品种（理财 / 存款 / 债券） */
export function hasMaturity(kind: string): boolean {
  return (MATURITY_KINDS as readonly string[]).includes(kind);
}

/** 该品种能不能记收益（`other` 没有收益入口 —— PRD §5 表格最后一行为空） */
export function canRecordIncome(kind: string): boolean {
  return actionsOf(kind).incomeKind !== null;
}

/** 已清仓（数量为 0 但持仓保留 —— `AC-INV7`） */
export function isCleared(v: HoldingView): boolean {
  return v.quantityE4 === 0;
}

// ─────────────────────────── ② 展示文本 ───────────────────────────

/**
 * 比率文本（浮盈率 / 总收益率 / 已实现收益率）。
 *
 * 🔴 `null` ⇒ `—`（`AC-INV7`：已清仓的浮盈率、无买入的总收益率都是 `null`）。
 * 显示成 `0%` 会让"算不出来"与"没赚没亏"混为一谈。
 */
export function returnTextOf(e4: number | null): string {
  return e4 === null ? '—' : percentWithSign(e4);
}

/** 占比文本（`weightE4`；同币种市值为 0 ⇒ `null` ⇒ `—`） */
export function weightTextOf(e4: number | null): string {
  return e4 === null ? '—' : percentWithoutSign(e4);
}

/** 份额文本（去尾零：`1000000` → `"100"`） */
export function qtyTextOf(v: HoldingView): string {
  return e4ToTrimmedText(v.quantityE4);
}

/** 价格文本（恒 4 位：`10300` → `"1.0300"`） */
export function priceTextOf(e4: number): string {
  return e4ToText(e4);
}

/** 持仓标题：名称 → 代码 → `（未命名）`（历史数据里三者都可能缺） */
export function holdingTitleOf(v: HoldingView): string {
  const name = v.name?.trim() ?? '';
  const code = v.code?.trim() ?? '';
  if (name !== '') return name;
  if (code !== '') return code;
  return '（未命名持仓）';
}

/** 行副标题：代码 · 品种（缺什么不写什么，不留空分隔符） */
export function holdingSubtitleOf(v: HoldingView): string {
  const parts: string[] = [];
  const code = v.code?.trim() ?? '';
  if (code !== '') parts.push(code);
  parts.push(kindLabelOf(v.kind));
  return parts.join(' · ');
}

/**
 * 到期文本（理财 / 存款 / 债券）。
 *
 * | 情形 | 文本 |
 * |---|---|
 * | 未填 | `到期日未填`（**不是**"不到期"—— 未填是缺数据，`AC-INV7` 同理） |
 * | 已过 | `已到期 3 天` |
 * | 今天 | `今天到期` |
 * | 未来 | `剩 45 天 · 2027-03-18` |
 *
 * 🔴 脏日期（`2026-13-01`）按**未填**处理，不拿归一化后的日期算天数：
 * `Date.UTC(2026, 12, 1)` 会静默变成 2027-01-01，于是"剩 69 天"看起来毫无异常
 * （`isRealDayString` 存在的理由，铁律 99）。
 */
export function maturityTextOf(maturityDate: string | null, today: string): string {
  if (maturityDate === null || !isRealDayString(maturityDate)) return MATURITY_UNFILLED_TEXT;
  const days = daysBetween(today, maturityDate);
  if (days < 0) return `已到期 ${String(-days)} 天`;
  if (days === 0) return '今天到期';
  return `剩 ${String(days)} 天 · ${maturityDate}`;
}

/**
 * 年化提示（`AC-INV12`）：数字与「持有天数 + 样本不足」**必须同屏**。
 *
 * 🔴 `thinSample`（< 90 天）时端侧用 `notice` 琥珀色，不用 `alert` 朱红 ——
 * 这不是错误，是一条"数学正确但会误导"的提示（铁律 8）。
 */
export function xirrHintOf(xirr: XirrView): string {
  return `${XIRR_HINT_PREFIX}${String(xirr.holdingDays)}${XIRR_HINT_SUFFIX}`;
}

/**
 * 该年化是否需要用警示色。
 *
 * 🔴 判据**只读服务端的 `thinSample`**，端侧不拿 `holdingDays` 再比一次 90 ——
 * 同一个门槛写两遍，将来调阈值（比如改成 180 天）就会一处改一处漏（铁律 24）。
 */
export function isThinSample(xirr: XirrView): boolean {
  return xirr.thinSample;
}

/** 该币种是否有历史口径差（`AC-INV9` 只对新交易强制闭合） */
export function hasClosingDiff(s: CurrencySummaryView): boolean {
  return s.closingDiffCents !== 0;
}

// ─────────────────────────── ③ 台账分组 ───────────────────────────

/** 一个平台（投资账户）下的持仓 */
export interface PlatformGroup {
  /** 分组键：账户 id；`null` 账户的持仓归到 `__none__` */
  readonly key: string;
  readonly accountName: string;
  readonly currency: string;
  readonly holdings: readonly HoldingView[];
}

/** 无平台持仓的分组键（历史数据里 `account_id` 可为空） */
export const NO_PLATFORM_KEY = '__none__';

/**
 * 台账分组：**平台 → 品种**（PRD §4.2）。
 *
 * - 平台顺序 = **首次出现顺序**（服务端已按币种排好持仓，端侧不重排平台 —— 重排会让
 *   「本币平台在前」这条服务端决定失效）；
 * - 组内按 `KIND_ORDER` 排（同品种保持原顺序）；
 * - `accountId === null` 的持仓单独一组，**不藏起来**（藏了就是"有 26 条持仓但只看到 25 条"）。
 */
export function groupByPlatform(holdings: readonly HoldingView[]): PlatformGroup[] {
  const groups = new Map<string, { name: string; currency: string; items: HoldingView[] }>();

  for (const h of holdings) {
    const key = h.accountId ?? NO_PLATFORM_KEY;
    const existing = groups.get(key);
    if (existing === undefined) {
      groups.set(key, {
        name: h.accountName ?? UNASSIGNED_PLATFORM,
        currency: h.currency,
        items: [h],
      });
    } else {
      existing.items.push(h);
    }
  }

  return [...groups.entries()].map(([key, g]) => ({
    key,
    accountName: g.name,
    currency: g.currency,
    holdings: [...g.items].sort((a, b) => kindRank(a.kind) - kindRank(b.kind)),
  }));
}

/** 品种排序位次（不在 `KIND_ORDER` 的排最后） */
function kindRank(kind: string): number {
  const i = KIND_ORDER.indexOf(kind);
  return i < 0 ? KIND_ORDER.length : i;
}

// ─────────────────────────── ④ 买卖草稿 ───────────────────────────

/** 交易方向（赎回 / 取出 / 兑付都走 `sell` —— `DR-059` §12.1） */
export type TradeMode = 'buy' | 'sell';

/**
 * 三个输入框 —— 🆕 `DR-072` `D6`。
 *
 * 净值 / 份额 / 总价三者的精度不同（`0.0001` / `0.01` / `0.01`），
 * 任意两项确定后第三项必然有舍入 ⇒ 三个都必须能填，且必须知道"哪个是算出来的"。
 */
export type TradeField = 'price' | 'qty' | 'amount';

/** 三字段的固定顺序（派生目标的兜底顺序，也是"哪些还没被编辑"的判据基） */
const TRADE_FIELDS: readonly TradeField[] = ['price', 'qty', 'amount'];

export interface TradeDraft {
  readonly mode: TradeMode;
  /** 卖出必填；买入为 `null`（新建）或已有持仓 id（加仓） */
  readonly holdingId: string | null;
  /** 品种：新建买入必填；卖出 / 加仓取持仓的品种 */
  readonly kind: string;
  readonly name: string;
  readonly code: string;
  /** 净值文本（元/份，最多 4 位小数） */
  readonly priceText: string;
  /** 份额文本（银行口径：最多 2 位小数） */
  readonly qtyText: string;
  /** 总价文本（元，最多 2 位小数）—— 成交金额 */
  readonly amountText: string;
  readonly feeText: string;
  readonly accountId: string;
  readonly date: string;
  readonly note: string;
  /** 到期日（仅理财 / 存款 / 债券；空 = 未填） */
  readonly maturityDate: string;
  /**
   * **锁定栈**（最近编辑的两个字段，顺序保留，容量 2）—— `DR-072` `D6`。
   *
   * 🔴 规则：编辑任一字段 ⇒ 它进栈、最早的那个出栈 ⇒ **不在栈里的那个**被重算。
   * 栈长 < 2 时不派生（用户才填了第一个数，此时"算另一个"只能是猜）。
   * ⇒ 结束状态恒定是「两个手填 + 一个派生」，而**最后编辑的永远是权威**。
   */
  readonly edited: readonly TradeField[];
  /**
   * 本次联动是否把份额**夹到了持仓上限**（`DR-072` `D5`）。
   *
   * 只有"由金额反算"这一路会置真 —— 手输超持仓仍然报错（不做卖空）。
   */
  readonly clamped: boolean;
}

/** 新建买入的空白草稿（币种与品种由页面选定；日期默认今天） */
export function emptyTradeDraft(today: string, kind: string = HoldingKind.Fund): TradeDraft {
  return {
    mode: 'buy',
    holdingId: null,
    kind,
    name: '',
    code: '',
    priceText: '',
    qtyText: '',
    amountText: '',
    feeText: '',
    accountId: '',
    date: today,
    note: '',
    maturityDate: '',
    edited: [],
    clamped: false,
  };
}

/**
 * 从一条持仓发起动作（卖出 / 加仓 / 改价前的预填）。
 *
 * 🔴 价格与账户**都预填**：卖出 90% 是"按现价卖"，让用户重敲一遍现价是多余的；
 * 加仓则预填品种与账户，但仍要用户填新的价格（加仓价不是现价）。
 * 预填的净值进锁定栈 ⇒ 用户接着填份额或总价时，第三个数会立刻联动出来。
 */
export function draftFromHolding(v: HoldingView, mode: TradeMode, today: string): TradeDraft {
  return {
    mode,
    holdingId: v.id,
    kind: v.kind,
    name: v.name ?? '',
    code: v.code ?? '',
    priceText: e4ToText(v.currentPriceE4),
    qtyText: '',
    amountText: '',
    feeText: '',
    accountId: v.accountId ?? '',
    date: today,
    note: '',
    maturityDate: v.maturityDate ?? '',
    edited: ['price'],
    clamped: false,
  };
}

// ─────────────────── 三框联动（`DR-072` D6 / `AC-INV17`） ───────────────────

/** 该字段当前是不是**派生**出来的（= 两个手填字段之外的第三个） */
function isDerived(draft: TradeDraft, field: TradeField): boolean {
  return draft.edited.length === 2 && !draft.edited.includes(field);
}

/** 份额是否由「总价 ÷ 净值」反算而来（提交时要告诉服务端，它据此决定"夹持还是报错"） */
export function isQtyDerived(draft: TradeDraft): boolean {
  return isDerived(draft, 'qty');
}

/** 净值是否由「总价 ÷ 份额」反算而来 —— 界面必须据此提示（误差 = `0.00005 × 份额`） */
export function isPriceDerived(draft: TradeDraft): boolean {
  return isDerived(draft, 'price');
}

/**
 * **编辑一个字段并按锁定栈联动**（`DR-072` `D6` · `AC-INV17`）。
 *
 * ```
 * edited = [最近两个字段]（本次编辑的排最后，最早的那个出栈）
 * 目标   = 三个字段里不在 edited 里的那个（栈满 2 时唯一）
 * 目标值 = 内核函数(edited[0], edited[1])     ← 端侧不自己除
 * ```
 *
 * ## 🔴 两条不联动的情形（都不是遗漏）
 *
 * 1. **栈长 < 2**：用户才填了第一个数，此时"另一个"只能猜 —— 猜错比不填更糟；
 * 2. **两个手填值不合法**（空 / 0 / 格式错）：派生不出来就不写，让校验层统一提示。
 *
 * @param holding 卖出目标持仓 —— 只有它能判"反算份额超持仓要夹持"，买单传 `null`
 */
export function applyTradeEdit(
  draft: TradeDraft,
  field: TradeField,
  value: string,
  holding: HoldingView | null = null,
): TradeDraft {
  const base: TradeDraft =
    field === 'price'
      ? { ...draft, priceText: value }
      : field === 'qty'
        ? { ...draft, qtyText: value }
        : { ...draft, amountText: value };

  const edited = [...base.edited.filter((f) => f !== field), field].slice(-2);
  const next: TradeDraft = { ...base, edited, clamped: false };
  if (edited.length < 2) return next;

  const target = TRADE_FIELDS.find((f) => !edited.includes(f));
  return target === undefined ? next : deriveTradeField(next, target, holding);
}

/** 把 `target` 用另外两个字段算出来（唯一"像计算"的地方，且全部调内核） */
function deriveTradeField(
  draft: TradeDraft,
  target: TradeField,
  holding: HoldingView | null,
): TradeDraft {
  const price = priceE4OrNull(draft.priceText);
  const qty = qtyE4OrNull(draft.qtyText);
  const amount = yuanTextToCentsOrNull(draft.amountText);

  if (target === 'amount') {
    if (price === null || qty === null) return draft;
    const cents = priceToCents(asQty(qty), asPrice(price)) as number;
    return { ...draft, amountText: centsToYuanText(toCents(cents)) };
  }

  if (target === 'qty') {
    if (price === null || amount === null) return draft;
    const derived = amountToQty2E4(amount as number, price) as number;
    if (derived <= 0) return draft;
    // 🔴 反算超持仓 = 舍入导致（不是用户算错）⇒ 夹到持仓，并**同时**改写总价（`D5`）
    if (holding !== null && derived > holding.quantityE4) {
      const cents = priceToCents(asQty(holding.quantityE4), asPrice(price)) as number;
      return {
        ...draft,
        qtyText: e4ToTrimmedText(holding.quantityE4),
        amountText: centsToYuanText(toCents(cents)),
        clamped: true,
      };
    }
    return { ...draft, qtyText: e4ToTrimmedText(derived) };
  }

  // target === 'price'：最不可靠的一条（一万份误差可达 0.5 元）⇒ 界面必须提示
  if (qty === null || amount === null) return draft;
  const derivedPrice = amountToPriceE4(amount as number, qty) as number;
  if (derivedPrice <= 0) return draft;
  return { ...draft, priceText: e4ToText(derivedPrice) };
}

/**
 * 「全部赎回」：把持仓的**精确份额**填进去并联动总价。
 *
 * 🔴 走 `applyTradeEdit`（而不是直接赋值）—— 这样它和手输共用同一条路径，
 * 也就自动享受"派生 + 夹持"两条规则，不会出现第二套动作。
 */
export function fillFullRedemption(draft: TradeDraft, holding: HoldingView): TradeDraft {
  return applyTradeEdit(draft, 'qty', e4ToTrimmedText(holding.quantityE4), holding);
}

/**
 * 提交前预览（三框 → 请求体的三个数 · `DR-072` `D2`）。
 *
 * 🔴 `amountCents` 是**权威**（用户填的总价），不是"份额 × 净值"的换算结果。
 */
export interface TradePreview {
  /** 份额（×10⁴） */
  readonly quantityE4: number;
  /** **成交金额**（整数分）：买入 = 实付、卖出 = 实收（不含手续费） */
  readonly amountCents: number;
  /** 本次净值（×10⁴）；卖出预览算成本/盈亏时要用（同一份解析结果，不重解一次） */
  readonly priceE4: number;
  /** 用户填的总价（整数分）；文本为空 ⇒ `null`（此时 `amountCents` 由乘积回退） */
  readonly inputAmountCents: number | null;
  /** 成交金额 ≠ 份额 × 净值 ⇒ 界面提示这笔"尾差"（`AC-INV18`） */
  readonly differsFromInput: boolean;
}

/** 解析价格文本 → E4（非法返回 `null`，由校验层给提示） */
function priceE4OrNull(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  try {
    const v = e4TextToInt(trimmed);
    return v > 0 ? v : null;
  } catch {
    return null;
  }
}

/** 解析份额文本 → E4（负数 / 0 / 非法 ⇒ `null`） */
function qtyE4OrNull(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  try {
    const v = e4TextToInt(trimmed);
    return v > 0 ? v : null;
  } catch {
    return null;
  }
}

/**
 * 文本的小数位数（**只看形态**，不做数值转换）。
 *
 * 🔴 用于"份额最多两位小数"这条银行口径（`DR-072` D1）——
 * 不能用 `e4TextToInt` 的结果去判：它对 4 位以内一律接受，看不出用户填了几位。
 */
function decimalsOf(text: string): number {
  const t = text.trim();
  const dot = t.indexOf('.');
  return dot < 0 ? 0 : t.length - dot - 1;
}

/**
 * 预览：把三框的当前文本折成 `(quantityE4, amountCents, priceE4)`。
 *
 * ## 🔴 `amountCents` 现在是**用户填的那个数**，不是算出来的
 *
 * `DR-072` `D2`：三个数精度不同、乘积必然有舍入 ⇒ 银行确认金额才是权威。
 * 所以：
 * - **份额与净值必须有**（缺一个就 `null`，不猜）；
 * - **总价可缺** —— 缺了就回退 `priceToCents(份额, 净值)`（与批 125 之前逐分一致，
 *   老调用点与老测试因此仍然成立）；
 * - `differsFromInput` 的含义随之变成"**成交金额 ≠ 份额 × 净值**"（也就是那笔尾差）。
 *
 * @returns 份额或净值解析不出来 ⇒ `null`（调用方显示"填完整才能预览"）
 */
export function previewTrade(draft: TradeDraft): TradePreview | null {
  const price = priceE4OrNull(draft.priceText);
  if (price === null) return null;
  const qty = qtyE4OrNull(draft.qtyText);
  if (qty === null) return null;

  const input = yuanTextToCentsOrNull(draft.amountText);
  const expected = priceToCents(asQty(qty), asPrice(price)) as number;
  const amount = input === null ? expected : (input as number);
  return {
    quantityE4: qty,
    amountCents: amount,
    priceE4: price,
    inputAmountCents: input === null ? null : (input as number),
    differsFromInput: amount !== expected,
  };
}

/** 校验所需的上下文（页面提供） */
export interface TradeContext {
  /** 卖出 / 加仓的目标持仓；新建为 `null` */
  readonly holding: HoldingView | null;
  /** 可选资金账户（**已过滤为 `type='investment'`**） */
  readonly accounts: readonly { readonly id: string; readonly name: string; readonly currency: string }[];
}

/**
 * 草稿校验 —— 只拦"表单里一眼看得出的不一致"。
 *
 * 🔴 **不在这里判的服务端约束**：卖出不超持仓由服务端终审（`AC-INV3`）、
 * 账户必须是 investment 类型由服务端判（`AC-INV1`）。端侧这里做的是"别让用户白跑一趟"，
 * 所以判据只取**表单自身的完整性** + **跨字段的明显矛盾**。
 */
export function validateTradeDraft(draft: TradeDraft, ctx: TradeContext): Record<string, string> {
  const errors: Record<string, string> = {};

  // 新建买入：必须有名称（契约层也这么判 —— 两处都给，用户才不会点两次才看到提示）
  if (draft.holdingId === null && draft.mode === TradeKind.Buy) {
    if (draft.name.trim() === '') errors.name = '新建持仓必须填名称';
    if (draft.kind.trim() === '') errors.kind = '请选择品种';
  }
  if (draft.mode === TradeKind.Sell && draft.holdingId === null) {
    errors.holdingId = '卖出必须指定持仓';
  }

  // 总价：**可空**（联动时自动填），填了就必须是正数（`DR-072` D2）
  if (draft.amountText.trim() !== '') {
    const amount = yuanTextToCentsOrNull(draft.amountText);
    if (amount === null) errors.amountText = '总价格式不对（最多两位小数）';
    else if ((amount as number) <= 0) errors.amountText = '总价必须大于 0';
  }

  // 份额：银行口径 0.01（`DR-072` D1）
  const qty = qtyE4OrNull(draft.qtyText);
  if (qty === null) {
    errors.qtyText = '份额格式不对（须大于 0）';
  } else {
    // 唯一豁免 = 恰好等于持仓精确值 ⇒ 「全部赎回」能清掉历史遗留的 4 位尾数
    const isHoldingExact = ctx.holding !== null && qty === ctx.holding.quantityE4;
    if (!isHoldingExact && decimalsOf(draft.qtyText) > 2) {
      errors.qtyText = '份额最多两位小数（银行口径 0.01）';
    } else if (
      draft.mode === TradeKind.Sell &&
      ctx.holding !== null &&
      qty > ctx.holding.quantityE4 &&
      // 由金额反算出的超持仓归服务端夹持（`DR-072` D5 / `AC-INV20`），端侧不拦
      !isQtyDerived(draft)
    ) {
      errors.qtyText = `超过持仓（只有 ${qtyTextOf(ctx.holding)} 份）`;
    }
  }

  if (priceE4OrNull(draft.priceText) === null) errors.priceText = '价格须大于 0，最多四位小数';

  // 三个数必须"对得上"（容差按净值放大）—— 服务端也判，端侧先拦一道（`AC-INV19`）
  const preview = previewTrade(draft);
  if (preview !== null) {
    const expected = priceToCents(asQty(preview.quantityE4), asPrice(preview.priceE4)) as number;
    const diff = preview.amountCents - expected;
    const tolerance = grossToleranceCents(preview.priceE4, preview.quantityE4);
    if (diff > tolerance || diff < -tolerance) {
      errors.amountText = `三个数对不上（与「份额 × 净值」相差 ${String(diff)} 分），请核对`;
    }
  }

  if (draft.feeText.trim() !== '' && yuanTextToCentsOrNull(draft.feeText) === null) {
    errors.feeText = '手续费格式不对（最多两位小数）';
  }

  if (draft.accountId.trim() === '') {
    errors.accountId = '请选择资金账户';
  } else {
    const account = ctx.accounts.find((a) => a.id === draft.accountId);
    if (account === undefined) {
      errors.accountId = '这个账户不在投资类账户里';
    } else if (ctx.holding !== null && account.currency !== ctx.holding.currency) {
      // 跨币种买卖本版不支持（`DR-004`）—— 服务端也拦，这里给"一眼可见"的提示
      errors.accountId = `币种不一致（${account.currency} ≠ ${ctx.holding.currency}）`;
    }
  }

  // 日期只验形态；真实性（`fromYmd(parseDay(d)) === d`）在服务端（铁律 99）
  if (!isDayString(draft.date)) errors.date = '日期格式应为 YYYY-MM-DD';
  if (draft.maturityDate.trim() !== '' && !isDayString(draft.maturityDate.trim())) {
    errors.maturityDate = '到期日格式应为 YYYY-MM-DD';
  }

  return errors;
}

/**
 * 构造 `POST /api/investments/trades` 的请求体。
 *
 * 🔴 **可选键只在有值时才出现**（铁律 63：不发 `undefined`）——
 * 这是服务端 Zod `.optional()` 与 `.nullish()` 的分界：多发一个 `name: null`
 * 在"新建买入"分支里会被 `.superRefine` 判成"没填名称"。
 *
 * ⚠️ 调用前必须过 {@link validateTradeDraft}（本函数不重复校验，只组装）。
 */
export function buildTradeRequest(draft: TradeDraft): CreateTradeRequest {
  const price = priceE4OrNull(draft.priceText)!;
  const preview = previewTrade(draft);
  const quantityE4 = preview?.quantityE4 ?? 0;
  const fee = draft.feeText.trim() === '' ? null : yuanTextToCentsOrNull(draft.feeText);
  const maturity = draft.maturityDate.trim();

  return {
    kind: draft.mode,
    ...(draft.holdingId === null ? {} : { holdingId: draft.holdingId }),
    // 新建（buy 且无 holdingId）才需要名称 / 代码 / 品种
    ...(draft.mode === TradeKind.Buy && draft.holdingId === null
      ? {
          name: draft.name.trim(),
          holdingKind: draft.kind as CreateTradeRequest['holdingKind'],
          ...(draft.code.trim() === '' ? {} : { code: draft.code.trim() }),
          ...(maturity === '' ? {} : { maturityDate: maturity }),
        }
      : {}),
    quantityE4,
    priceE4: price,
    // 🔴 成交金额是权威（`DR-072` D2）：`preview` 缺省时回退乘积，所以这里总能给出一个数
    ...(preview === null ? {} : { grossAmountCents: preview.amountCents }),
    // 份额是反算来的 ⇒ 服务端在超持仓时按"全部赎回"夹持而不是 400（`D5`）
    ...(isQtyDerived(draft) ? { qtyDerivedFromAmount: true } : {}),
    ...(fee === null || (fee as number) <= 0 ? {} : { feeCents: fee as number }),
    accountId: draft.accountId,
    date: draft.date,
    ...(draft.note.trim() === '' ? {} : { note: draft.note.trim() }),
  };
}

// ─────────────────────────── ⑤ 收益草稿 ───────────────────────────

export interface IncomeDraft {
  readonly holdingId: string;
  readonly kind: 'dividend' | 'interest';
  readonly amountText: string;
  readonly date: string;
  readonly note: string;
}

/** 从持仓发起「记收益」（默认取该品种对应的收益类型） */
export function emptyIncomeDraft(v: HoldingView, today: string): IncomeDraft {
  return {
    holdingId: v.id,
    kind: actionsOf(v.kind).incomeKind ?? IncomeKind.Dividend,
    amountText: '',
    date: today,
    note: '',
  };
}

/** 收益草稿校验（金额必须为正 —— 分红为负是"倒扣"，本版不支持） */
export function validateIncomeDraft(draft: IncomeDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.holdingId.trim() === '') errors.holdingId = '必须指定持仓';
  const amount = yuanTextToCentsOrNull(draft.amountText);
  if (amount === null) errors.amountText = '收益金额格式不对（最多两位小数）';
  else if ((amount as number) <= 0) errors.amountText = '收益金额必须大于 0';
  if (!isDayString(draft.date)) errors.date = '日期格式应为 YYYY-MM-DD';
  return errors;
}

/** 构造 `POST /api/investments/incomes` 的请求体 */
export function buildIncomeRequest(draft: IncomeDraft): RecordInvestmentIncome {
  const amount = yuanTextToCentsOrNull(draft.amountText);
  return {
    holdingId: draft.holdingId,
    kind: draft.kind,
    amountCents: amount as number,
    date: draft.date,
    ...(draft.note.trim() === '' ? {} : { note: draft.note.trim() }),
  };
}

// ─────────────────────────── ⑥ 改价 ───────────────────────────

/** 单条改价请求；价格非法 ⇒ `null`（按钮据此禁用，不靠异常兜底） */
export function buildPriceRequest(text: string): UpdateHoldingPriceRequest | null {
  const price = priceE4OrNull(text);
  return price === null ? null : { currentPriceE4: price };
}

/** 批量改价的一项（页面上"留空 = 这条不改"） */
export interface BatchPriceEntry {
  readonly holdingId: string;
  readonly text: string;
}

/**
 * 批量改价请求。
 *
 * 🔴 三条判据都在这：① 留空的跳过（"只填要改的"）；② 全空 ⇒ `null`（契约 `items.min(1)`，
 * 发空数组是 400，而且用户点了按钮却什么都没发生会更困惑）；③ 任一价格非法 ⇒ `null`
 * （**不**跳过非法项 —— 静默忽略用户敲进去的值比报错更糟）。
 */
export function buildBatchPriceRequest(entries: readonly BatchPriceEntry[]): BatchUpdatePriceRequest | null {
  const items: { holdingId: string; currentPriceE4: number }[] = [];
  for (const e of entries) {
    if (e.text.trim() === '') continue;
    const price = priceE4OrNull(e.text);
    if (price === null) return null;
    items.push({ holdingId: e.holdingId, currentPriceE4: price });
  }
  if (items.length === 0) return null;
  return { items };
}

/** 改价按钮是否可点（价格文本合法） */
export function canSubmitPrice(text: string): boolean {
  return buildPriceRequest(text) !== null;
}

/** 批量改价按钮是否可点（至少一条有值且全部合法） */
export function canSubmitBatchPrice(entries: readonly BatchPriceEntry[]): boolean {
  return buildBatchPriceRequest(entries) !== null;
}

// ─────────────────────────── ⑦ 口径对账（批 99 B3） ───────────────────────────

/**
 * 对账结论的**一句话解释**。
 *
 * ## 🔴 为什么端侧要"解释"而不是只显示差额
 *
 * 用户看到「差 ¥7,393.51」只会得出一个结论：算错了。而实际差额有**三种都正常**的来源
 * （`DR-059` D7 的历史数据、分类被 R 域共用、真的是 bug）。所以页面必须把这句
 * 从差额**推出来**的判断一起给出 —— 但它是**读差值的意义**，不是重新算一遍账：
 * 所有数字仍来自 `ReconcileCurrencyView` 的现成字段。
 *
 * ## 判定顺序（顺序本身是规则）
 *
 * ```
 * ① 不可比            ⇒ notComparable（原样展示服务端给的原因）
 * ② 两个差额都是 0    ⇒ ok
 * ③ 报表侧全 0、投资侧有 ⇒ history（迁移数据没有资金腿 —— 最常见）
 * ④ 报表比投资多      ⇒ shared（`cat-invest-gain` 被 R 域共用）
 * ⑤ 其余              ⇒ unknown（请人工核对）
 * ```
 */
export type ReconcileNoteKind = 'ok' | 'history' | 'shared' | 'unknown' | 'notComparable';

export interface ReconcileNote {
  kind: ReconcileNoteKind;
  text: string;
  /** 需要警示色（琥珀）—— `ok` 之外都为 true */
  attention: boolean;
}

export function reconcileNoteOf(row: ReconcileCurrencyView): ReconcileNote {
  if (!row.comparable) {
    return { kind: 'notComparable', text: row.reason ?? RECONCILE_NOT_COMPARABLE, attention: true };
  }
  if (row.reconciled) return { kind: 'ok', text: RECONCILE_OK, attention: false };

  const reportTotal = row.reportGainCents + row.reportLossCents;
  const domainTotal = row.domainGainCents + row.domainLossCents;
  if (reportTotal === 0 && domainTotal > 0) {
    return { kind: 'history', text: RECONCILE_NOTE_HISTORY, attention: true };
  }
  if (row.gainDiffCents > 0 || row.lossDiffCents > 0) {
    return { kind: 'shared', text: RECONCILE_NOTE_SHARED, attention: true };
  }
  return { kind: 'unknown', text: RECONCILE_NOTE_UNKNOWN, attention: true };
}

/**
 * 对账结论的**短标签**（Chip 用）：`一致` / `不可比` / `收益 ¥−73.94 · 亏损 ¥+0.01`。
 *
 * 🔴 与 {@link reconcileNoteOf} 分工：这个给"差多少"，那个给"为什么差"。
 * 两句都出现 —— 只给差额等于让用户自己猜，只给原因等于不给数。
 */
export function reconcileVerdictOf(row: ReconcileCurrencyView): string {
  if (!row.comparable) return RECONCILE_NOT_COMPARABLE;
  if (row.reconciled) return RECONCILE_OK;

  const parts: string[] = [];
  if (row.gainDiffCents !== 0) {
    parts.push(
      `${RECONCILE_GAIN} ${formatMoneyText(toCents(row.gainDiffCents), row.currency, { signed: true })}`,
    );
  }
  if (row.lossDiffCents !== 0) {
    parts.push(
      `${RECONCILE_LOSS} ${formatMoneyText(toCents(row.lossDiffCents), row.currency, { signed: true })}`,
    );
  }
  return parts.length === 0 ? RECONCILE_OK : parts.join(' · ');
}
